import { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import TranslateIcon from '@mui/icons-material/TranslateRounded';
import Button from '@components/Button';
import { Switch } from '@components/Switch';
import { Typography } from '@components/Typography';
import { SectionTitle, Hint } from '@features/ChannelsForms/FormSection';
import FieldWithActions, { AddRowButton, rowDeleteAction } from '@components/FieldWithActions';
import BusinessHoursField from '@features/BusinessHoursField';
import LocalizeItemModal from '@features/LocalizeItemModal';
import LocalizedFieldInput from '@features/LocalizeItemModal/LocalizedFieldInput';
import LocalizedTexts from '@features/LocalizeItemModal/LocalizedTexts';
import { useTranslation } from '@hooks';
import {
    BUTTON_KEYS,
    MAX_WELCOME_BUTTONS,
    OFFLINE_MESSAGE_FIELDS,
    WELCOME_MESSAGE_FIELD,
    fillTextRow,
    hasEveryTextKey,
    welcomeButtonFields,
} from '@constants/liveChat';

const Container = styled('div')({
    display: 'grid',
    gridGap: '8px',
    gridTemplateColumns: '100%',
});

const ButtonRows = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    marginBottom: 8,
});

const LiveChatMessages = () => {
    const { t } = useTranslation();
    const { control, setValue, watch } = useFormContext();
    const [isLocalizeOpen, setIsLocalizeOpen] = useState(false);
    const [touched, setTouched] = useState({});

    const texts = watch('texts') || [];
    const buttonCount = Math.min(watch('buttonCount') || 0, MAX_WELCOME_BUTTONS);
    const workingHoursEnabled = watch('workingHoursEnabled');

    useEffect(() => {
        if (!texts.every(hasEveryTextKey)) {
            setValue('texts', texts.map(fillTextRow));
        }
    }, [texts]);

    const primary = texts[0];
    const fallbackLanguage = primary?.language;
    const buttonFields = welcomeButtonFields(buttonCount);

    if (!primary) return null;

    const touch = name => setTouched(prev => (prev[name] ? prev : { ...prev, [name]: true }));

    const updateTexts = update => setValue('texts', texts.map(update), { shouldDirty: true, shouldValidate: true });

    const onChangePrimary = e => {
        const { name, value } = e.target;
        updateTexts(text => (text.language === primary.language ? { ...text, [name]: value } : text));
        touch(name);
    };

    const setButtonCount = count => setValue('buttonCount', count, { shouldDirty: true, shouldValidate: true });

    const addButton = () => setButtonCount(buttonCount + 1);

    const removeButton = index => {
        updateTexts(text => {
            const row = { ...text };
            BUTTON_KEYS.slice(index).forEach((key, offset) => {
                row[key] = row[BUTTON_KEYS[index + offset + 1]] || '';
            });
            return row;
        });
        setButtonCount(buttonCount - 1);
    };

    const renderPrimaryField = field => (
        <LocalizedFieldInput
            key={field.name}
            field={field}
            value={primary[field.name]}
            onChange={onChangePrimary}
            onBlur={() => touch(field.name)}
            touched={!!touched[field.name]}
        />
    );

    return (
        <Container>
            <SectionTitle>{t('SettingsChannels.welcomeTagline')}</SectionTitle>
            {renderPrimaryField(WELCOME_MESSAGE_FIELD)}
            <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                {t('SettingsChannels.welcomeButtons')}
            </Typography>
            {buttonCount > 0 ? (
                <ButtonRows>
                    {buttonFields.map((field, index) => (
                        <FieldWithActions
                            key={field.name}
                            actions={rowDeleteAction({ onDelete: () => removeButton(index), t })}
                        >
                            {renderPrimaryField(field)}
                        </FieldWithActions>
                    ))}
                </ButtonRows>
            ) : null}
            {buttonCount < MAX_WELCOME_BUTTONS ? (
                <div style={{ marginBottom: 8 }}>
                    <AddRowButton label={t('SettingsChannels.addButton')} onClick={addButton} />
                </div>
            ) : null}
            <div>
                <Button variant="outlined" startIcon={<TranslateIcon />} onClick={() => setIsLocalizeOpen(true)}>
                    {t('buttons.localization')}
                </Button>
            </div>
            <Controller
                control={control}
                name="welcomePopupEnabled"
                render={({ field }) => (
                    <Switch
                        checked={!!field.value}
                        handleChange={event =>
                            setValue('welcomePopupEnabled', event.target.checked, { shouldDirty: true })
                        }
                        label={t('SettingsChannels.welcomePopupSwitch')}
                    />
                )}
            />
            <Hint sx={{ marginTop: '-4px' }}>{t('SettingsChannels.welcomePopupHint')}</Hint>
            <SectionTitle sx={{ marginTop: '16px' }}>{t('SettingsChannels.offlineMessage')}</SectionTitle>
            <Controller
                control={control}
                name="workingHoursEnabled"
                render={({ field }) => (
                    <Switch
                        checked={!!field.value}
                        handleChange={event =>
                            setValue('workingHoursEnabled', event.target.checked, {
                                shouldDirty: true,
                                shouldValidate: true,
                            })
                        }
                        label={t('SettingsChannels.showOfflineMessage')}
                    />
                )}
            />
            {workingHoursEnabled ? (
                <>
                    <Controller
                        control={control}
                        name="workingHours"
                        render={({ field }) => (
                            <BusinessHoursField
                                value={field.value}
                                onChange={value => setValue('workingHours', value, { shouldDirty: true })}
                                hint={t('SettingsChannels.offlineHoursHint')}
                            />
                        )}
                    />
                    <LocalizedTexts
                        fields={OFFLINE_MESSAGE_FIELDS}
                        currentLocale={fallbackLanguage}
                        additionalRowsOptional
                    />
                </>
            ) : null}
            {isLocalizeOpen ? (
                <LocalizeItemModal
                    modal={{ open: true }}
                    entity="texts"
                    fields={[WELCOME_MESSAGE_FIELD, ...buttonFields]}
                    currentLocale={fallbackLanguage}
                    withChangeTitle={false}
                    additionalRowsOptional
                    onClose={() => setIsLocalizeOpen(false)}
                />
            ) : null}
        </Container>
    );
};

export default LiveChatMessages;
